import { UserLoginState } from '@sola/schema/dotcom/auth.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemLegacyGroupEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import type {
  CheckoutBillingInfo,
  CheckoutPaymentTypeEnum,
} from '@sola/schema/dotcom/checkout/billingInfo.ts';
import type { CheckoutContactInfo } from '@sola/schema/dotcom/checkout/contactInfo.ts';

import type { AnalyticsCartItem } from './analytics.ts';
import { mapAnalyticsCartItems, sendAnalyticsEvent } from './analytics.ts';
import { CURRENCY_USD } from './constants.ts';

export const events = {
  initialPush: 'initialPush',
  createAccount: 'sign-up',
  login: 'login',
  cartAdd: 'add_to_cart',
  cartRemove: 'remove_from_cart',
  cartView: 'view_cart',
  checkoutBegin: 'begin_checkout',
  checkoutSuccess: 'purchase',
  checkoutCommitmentPayment: 'commitment_payment',
  checkoutFundDonation: 'fund_donation',
  checkoutFundCommitment: 'fund_commitment',
  checkoutGiftPayment: 'gift_payment',
  cartAddPaymentInfo: 'add_payment_info',
  cartAddShippingInfo: 'add_shipping_info',
};

/**
 * This event is meant to send an initial event on page load.
 * @param contactId The id of the user.
 * @param supporterGlobalId The global id of the supporter.
 * @param sourceCodeTransactionId The source code id for a transaction.
 * @param sourceCodeCampaignId The source code id for a campaign.
 * @param isLoggedIn If the user is logged in or not.
 */
export const sendInitialPushEvent = (
  contactId: string,
  supporterGlobalId: string,
  sourceCodeTransactionId: string,
  sourceCodeCampaignId: string,
  isLoggedIn = false,
  loggedInState = UserLoginState.Guest,
) => {
  const data = {
    pageCategory: 'responsive',
    isLoggedIn,
    loggedInState,
    userId: contactId,
    supporterGlobalId,
    sourceCodeId: sourceCodeTransactionId,
    sourceCodeCampaign: sourceCodeCampaignId,
  };

  sendAnalyticsEvent(events.initialPush, data);
};

export const sendCreateAccountEvent = (isAccountCreatedWithEmail = false) => {
  const data = {
    method: isAccountCreatedWithEmail ? 'email address' : 'supporter global id',
  };

  sendAnalyticsEvent(events.createAccount, data);
};

export const sendLoginEvent = () => {
  sendAnalyticsEvent(events.login);
};

export const sendCartAddEvent = (
  addedItems: CartItem[],
  cartId: Cart['id'],
) => {
  if (!addedItems?.length) {
    return;
  }

  const cartItems = mapAnalyticsCartItems(addedItems);
  let value = 0;

  for (const item of cartItems) {
    value += item.price * item.quantity;
  }

  const data = {
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cartId,
    value,
    engagement_time_msec: 0,
  };

  sendAnalyticsEvent(events.cartAdd, data);
};

export const sendCartRemoveEvent = (removedItems: CartItem[]) => {
  if (!removedItems || !removedItems.length) {
    return;
  }

  const cartItems = mapAnalyticsCartItems(removedItems);
  let value = 0;

  for (const item of cartItems) {
    value += item.price * item.quantity;
  }

  const data = {
    items: cartItems,
    currency: CURRENCY_USD,
    value,
  };

  sendAnalyticsEvent(events.cartRemove, data);
};

export const sendCartViewEvent = (cart: Cart) => {
  const cartItems = mapAnalyticsCartItems(cart.items);
  const data = {
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cart.id,
    value: cart.totalToday,
    engagement_time_msec: 0,
  };

  sendAnalyticsEvent(events.cartView, data);
};

export const sendCheckoutBeginEvent = (
  cart: Cart,
  rateType?: 'standard' | 'introductory',
) => {
  const cartItems = mapAnalyticsCartItems(cart.items);
  const offerDualRate = cart.campaign?.offerDualRate ?? false;
  const data = {
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cart.id,
    value: cart.totalToday,
    engagement_time_msec: 0,
    ...(rateType && { rate_type: rateType }),
    ...(offerDualRate !== undefined && {
      offer_dual_rate: offerDualRate,
    }),
  };

  sendAnalyticsEvent(events.checkoutBegin, data);
};

const sendCheckoutSuccessKeyEvent = (
  item: AnalyticsCartItem,
  cartId: string,
) => {
  let eventName;

  switch (item.item_category) {
    case CartItemLegacyGroupEnum.CommitmentPayment: {
      eventName = events.checkoutCommitmentPayment;
      break;
    }

    case CartItemLegacyGroupEnum.Fund: {
      eventName =
        item.item_variant === 'OneTime'
          ? events.checkoutFundDonation
          : events.checkoutFundCommitment;
      break;
    }

    case CartItemLegacyGroupEnum.GiftPayment: {
      eventName = events.checkoutGiftPayment;
      break;
    }
  }

  if (!eventName) {
    return;
  }

  const data = {
    items: [item],
    currency: CURRENCY_USD,
    transaction_id: cartId,
    value: item.price * item.quantity,
    engagement_time_msec: 0,
  };

  sendAnalyticsEvent(eventName, data);
};

export const sendCheckoutSuccessEvent = (
  cart: Cart,
  rateType?: 'standard' | 'introductory',
) => {
  const cartItems = mapAnalyticsCartItems(cart.items);

  if (!cartItems.length) {
    return;
  }

  const data = {
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cart.id,
    value: cart.totalToday,
    engagement_time_msec: 0,
    sponsorships: 0,
    fund_commitments: 0,
    fund_donations: 0,
    gift_payments: 0,
    commitment_payments: 0,
    ...(rateType && { rate_type: rateType }),
  };

  for (const item of cartItems) {
    sendCheckoutSuccessKeyEvent(item, cart.id);

    switch (item.item_category) {
      case CartItemLegacyGroupEnum.ChildSelected:
      case CartItemLegacyGroupEnum.FirstPayment:
      case CartItemLegacyGroupEnum.EPayment: {
        data.sponsorships += 1;
        break;
      }

      case CartItemLegacyGroupEnum.CommitmentPayment: {
        data.commitment_payments += 1;
        break;
      }

      case CartItemLegacyGroupEnum.Fund: {
        if (item.item_variant === 'OneTime') {
          data.fund_donations += 1;
        } else {
          data.fund_commitments += 1;
        }
        break;
      }

      case CartItemLegacyGroupEnum.GiftPayment: {
        data.gift_payments += 1;
        break;
      }
    }
  }

  sendAnalyticsEvent(events.checkoutSuccess, data);
};

export const sendCheckoutAddPaymentInfoEvent = (
  paymentType: CheckoutPaymentTypeEnum,
  cart: Cart,
  details?: {
    paymentTypeClass?: ApplePayJS.ApplePayPaymentMethodSelectedEvent['paymentMethod']['type'];
  },
) => {
  const cartItems = mapAnalyticsCartItems(cart.items);
  const data = {
    currency: CURRENCY_USD,
    payment_type: paymentType,
    value: cart.totalToday,
    items: cartItems,
    ...(details?.paymentTypeClass && {
      payment_type_class: details.paymentTypeClass,
    }),
  };

  sendAnalyticsEvent(events.cartAddPaymentInfo, data);
};

export const sendCheckoutAddShippingInfoEvent = (
  cart: Cart,
  shippingInfo?: {
    city?: CheckoutBillingInfo['billingCity'];
    country?: CheckoutBillingInfo['billingCountry'] | string;
    firstName?: CheckoutContactInfo['firstName'];
    lastName?: CheckoutContactInfo['lastName'];
    phone?: CheckoutContactInfo['phone'];
    state?: CheckoutBillingInfo['billingState'];
    zip?: CheckoutBillingInfo['billingPostal'];
  },
) => {
  sendAnalyticsEvent(events.cartAddShippingInfo, {
    currency: CURRENCY_USD,
    value: cart.totalToday,
    items: mapAnalyticsCartItems(cart.items),
    supporter_city: shippingInfo?.city,
    supporter_country: shippingInfo?.country,
    supporter_first_name: shippingInfo?.firstName,
    supporter_last_name: shippingInfo?.lastName,
    supporter_phone: shippingInfo?.phone,
    supporter_state: shippingInfo?.state,
    supporter_zip: shippingInfo?.zip,
  });
};
